import React from 'react';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { Box, Stack } from '@mui/material';
import TableSkeleton from '../TableSkeleton';

/**
 * 공통 DataGrid 컴포넌트
 * 
 * 프로젝트 전반에서 사용되는 DataGrid의 공통 스타일과 설정을 제공합니다.
 * 
 * @param {Object} props
 * @param {Array} props.rows - 그리드에 표시할 행 데이터
 * @param {Array} props.columns - 그리드 컬럼 정의
 * @param {boolean} props.loading - 로딩 상태
 * @param {Function} props.getRowId - 행 ID를 반환하는 함수
 * @param {number} [props.height=600] - 그리드 높이 (px)
 * @param {boolean} [props.disableRowSelectionOnClick=true] - 행 클릭 시 선택 비활성화
 * @param {string} [props.paginationMode='server'] - 페이지네이션 모드 ('server' | 'client' | false)
 * @param {Object} [props.paginationModel] - 페이지네이션 모델
 * @param {Function} [props.onPaginationModelChange] - 페이지네이션 모델 변경 핸들러
 * @param {number} [props.rowCount] - 전체 행 개수 (서버 사이드 페이지네이션용)
 * @param {Array} [props.pageSizeOptions=[10, 20, 50, 100]] - 페이지 크기 옵션
 * @param {Function} [props.onRowClick] - 행 클릭 핸들러
 * @param {Function} [props.onCellClick] - 셀 클릭 핸들러
 * @param {Object} [props.sx] - 추가 스타일
 * @param {Object} [props.slotProps] - 슬롯 props
 * @param {Object} [props.initialState] - 초기 상태
 * @param {string} [props.noRowsMessage='데이터가 존재하지 않습니다.'] - 데이터 없을 때 메시지
 * @param {...Object} props.other - MUI DataGrid의 기타 props
 */
const DataGrid = ({
  rows,
  columns,
  loading,
  getRowId,
  height = 'auto',
  disableRowSelectionOnClick = true,
  paginationMode = 'server',
  pagination,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  pageSizeOptions = [10, 20, 50, 100],
  onRowClick,
  onCellClick,
  sx = {},
  slotProps,
  initialState,
  noRowsMessage = '데이터가 존재하지 않습니다.',
  ...other
}) => {
  // 페이지네이션 설정
  const paginationProps = pagination === false || paginationMode === false
    ? { pagination: false, hideFooter: true }
    : {
        paginationMode,
        paginationModel,
        onPaginationModelChange,
        rowCount,
        pageSizeOptions,
      };

  // 기본 스타일
  const defaultSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f8f9fa',
      color: '#333',
      fontSize: '14px',
      fontWeight: 700,
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: '#f5f5f5',
      cursor: onRowClick ? 'pointer' : 'default',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflowX: 'auto',
      overflowY: 'auto',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      overflow: 'visible',
    },
    ...sx,
  };

  // Box 높이 설정 (auto일 경우 제거)
  const boxSx = height === 'auto' 
    ? { width: '100%' }
    : { height, width: '100%' };

  return (
    <Box sx={boxSx}>
      <MuiDataGrid
        rows={rows ?? []}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        {...paginationProps}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        sx={defaultSx}
        slotProps={slotProps}
        initialState={initialState}
        slots={{
          loadingOverlay: () => (
            <TableSkeleton 
              row={paginationModel?.pageSize || 10} 
            />
          ),
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center" color="#888">
              {noRowsMessage}
            </Stack>
          ),
          ...(other.slots || {}),
        }}
        {...other}
      />
    </Box>
  );
};

export default DataGrid;
